import ActiveTag from "@components/common/ActiveTag";
import { RAQ_FORM_TYPES, STORAGE_LIKES } from "@components/utils/constants";
import {
  IAccommodationResponse,
  IActivityResponse,
  ILocationResponse,
  IPackageResponse,
} from "@customTypes/modelResponses";
import useAppContext from "@hooks/useAppContext";
import { ITripPreferencesProps } from "@modules/requestAQuote/RaQFullForm";
import RaQStickyContainer from "@modules/requestAQuote/RaQStickyContainer";
import dynamic from "next/dynamic";
import { RaQFormType } from "pages/things-to-do/[...slug]";
import {
  LegacyRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FiltersContext } from "../../providers/FiltersProvider";
const LocationIcon = dynamic(
  async () => await import("@components/icons/LocationIcon")
);
const CalendarIcon = dynamic(
  async () => await import("@components/icons/CalendarIcon")
);
const CalendarPopUpHomePage = dynamic(
  async () => await import("@components/CalendarPopUpHomePage")
);

interface IRequestQuoteFormProps {
  onRequestOpen: (isOpen: boolean) => void;
  packageData?:
    | IAccommodationResponse
    | ILocationResponse
    | IPackageResponse
    | IActivityResponse;
  initializeRequestQuote: () => void;
  raqFormType: RaQFormType;
  phone: {
    phoneAction: string;
    phoneNumber: string | undefined;
  };
  tripPreferencesProps: ITripPreferencesProps;
}

export const RequestQuoteForm = ({
  onRequestOpen,
  packageData,
  initializeRequestQuote,
  raqFormType,
  phone,
  tripPreferencesProps,
}: IRequestQuoteFormProps): JSX.Element => {
  const appContext = useAppContext();
  const filtersContext = useContext(FiltersContext);
  const [calendarError, setCalendarError] = useState(false);

  const calendarRef = useRef<HTMLDivElement>();

  const {
    showCalendar,
    setShowCalendar,
    monthsShown,
    inputDate,
    startDateState,
    setStartDate,
    endDateState,
    setEndDate,
    resetToLastValues,
  } = filtersContext!.calendarUtils;

  // Mobile devices (both Android and iOS) give initial value of dateWithNights string of "null"
  useEffect(() => {
    if (startDateState && endDateState) {
      setCalendarError(false);
    }
  }, [startDateState, endDateState]);

  const rewriteRAQDates = (): void => {
    const CURRENT_RAQ_STATE =
      // @ts-expect-error
      JSON.parse(localStorage.getItem("USER_REQUEST_QUOTE")) || {};
    CURRENT_RAQ_STATE.startDate = startDateState?.format("YYYY-MM-DD");
    CURRENT_RAQ_STATE.endDate = endDateState?.format("YYYY-MM-DD");
    localStorage.setItem(
      "USER_REQUEST_QUOTE",
      JSON.stringify(CURRENT_RAQ_STATE)
    );
  };

  const continueHandler = useCallback(() => {
    if (!startDateState || !endDateState) {
      setCalendarError(true);
      calendarRef?.current?.scrollIntoView();
      return;
    }
    initializeRequestQuote();
    rewriteRAQDates();
    onRequestOpen(true);
  }, [startDateState, endDateState, initializeRequestQuote, rewriteRAQDates]);

  const calendarProps = {
    setShowCalendar,
    inputDate,
  };

  return !appContext.isMobile ? (
    raqFormType !== RAQ_FORM_TYPES.FORM_B ? (
      <div className={`flex justify-start`}>
        <div className="relative h-auto w-[423px] rounded-2xl bg-blue-5">
          <div className="mx-[40px]">
            <span className="mb-8 pt-12 text-center text-h6 font-medium text-darkgray">
              Request a Quote
            </span>
            <div className="flex">
              <p className="mb-[6px] text-p font-medium text-darkgray">
                Dates*
              </p>
            </div>
            <div>
              <div className="relative">
                <div
                  ref={calendarRef as LegacyRef<HTMLDivElement>}
                  className="hidden min-w-[170px] items-center desktop:flex"
                >
                  {showCalendar && (
                    <div className="absolute top-0 left-[-35%]">
                      <CalendarPopUpHomePage
                        monthsShown={monthsShown}
                        calendarStatus={showCalendar}
                        setCalendarStatus={setShowCalendar}
                        startDate={startDateState}
                        setStartDate={setStartDate}
                        endDate={endDateState}
                        setEndDate={setEndDate}
                        counter={3}
                        handleSearch={() => {}}
                        isOnRequestQuote={true}
                        resetToLastValues={resetToLastValues}
                      />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowCalendar(true);
                  }}
                  className={`flex h-14 w-full items-center rounded-full bg-white px-[25px] ${
                    calendarError ? "error-field" : ""
                  }`}
                >
                  <CalendarIcon />
                  <input
                    type="text"
                    className="ml-[11px] w-full cursor-pointer text-black"
                    id="datepicker"
                    autoComplete="off"
                    readOnly
                    value={inputDate as string}
                    placeholder={"Add dates"}
                  />
                </button>
              </div>
              {calendarError && (
                <span className="error-text">{"Dates field is required"}</span>
              )}
            </div>
            <div className="mt-6 flex">
              <p className="mb-[6px] text-p font-medium text-darkgray">
                Places You&apos;ll Visit
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              {packageData ? (
                "locations" in packageData && packageData.locations ? (
                  packageData.locations.map((location) => {
                    return (
                      <div
                        key={location.id}
                        className="flex items-center gap-2"
                      >
                        <div className="relative flex h-[0.813rem] w-[0.625rem] items-center stroke-darkgray">
                          <LocationIcon />
                        </div>
                        <div className="text-darkgray">{location.Title}</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex gap-2">
                    <div className="h-[0.813rem] w-[0.625rem] stroke-darkgray">
                      <LocationIcon />
                    </div>
                    <div className="text-darkgray">{packageData?.Title}</div>
                  </div>
                )
              ) : (
                <></>
              )}
            </div>

            <div className="mt-6 flex">
              <p className="mb-[6px] text-p font-medium text-darkgray">
                Where To Stay
              </p>
            </div>

            <div className="flex flex-wrap">
              {tripPreferencesProps.accommodationTags.length > 0 &&
                tripPreferencesProps.accommodationTags.map((row) => (
                  <ActiveTag
                    key={`${row.id}${row.propertyCategory}`}
                    tagData={row}
                    onClick={tripPreferencesProps.handleRemoveTag as () => void}
                    RaQSection={STORAGE_LIKES.accommodations}
                  />
                ))}
            </div>

            <div className="mt-6 flex">
              <p className="mb-[6px] text-p font-medium text-darkgray">
                Things To Do
              </p>
            </div>
            <div className="flex flex-wrap">
              {tripPreferencesProps.autoPlacesTags.length > 0 &&
                tripPreferencesProps.autoPlacesTags.map((row) => (
                  <ActiveTag
                    key={`${row.id}${row.type}`}
                    tagData={row}
                    onClick={tripPreferencesProps.handleRemoveTag as () => void}
                    RaQSection={STORAGE_LIKES.activities}
                  />
                ))}
            </div>
            <div className="mt-8 flex">
              <h3 className="mb-[6px] font-medium text-darkgray">Important</h3>
            </div>
            <div className="mb-[8rem] flex flex-wrap">
              {tripPreferencesProps.importantTags.length > 0 &&
                tripPreferencesProps.importantTags.map((tag) => (
                  <ActiveTag
                    key={`${tag.id}${tag.type}`}
                    tagData={tag}
                    onClick={tripPreferencesProps.handleRemoveTag as () => void}
                  />
                ))}
            </div>
            <button
              className="absolute bottom-10 left-10 mx-auto flex w-[342px] justify-center rounded-full bg-orange p-[1.375rem] font-semibold text-white"
              onClick={continueHandler}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    ) : (
      <>
        <RaQStickyContainer
          calendarInputProps={calendarProps}
          isMobile={appContext?.isMobile}
          tripPreferencesProps={tripPreferencesProps}
          phone={phone}
          dates={{ startDate: startDateState, endDate: endDateState }}
        />
        <div
          ref={calendarRef as LegacyRef<HTMLDivElement>}
          className="hidden min-w-[170px] items-center desktop:flex"
        >
          {showCalendar && (
            <div className="absolute top-0 left-[-35%]">
              <CalendarPopUpHomePage
                calendarStatus={showCalendar}
                setCalendarStatus={setShowCalendar}
                monthsShown={monthsShown}
                startDate={startDateState}
                setStartDate={setStartDate}
                endDate={endDateState}
                setEndDate={setEndDate}
                counter={3}
                handleSearch={() => {}}
                isOnRequestQuote={true}
                resetToLastValues={resetToLastValues}
              />
            </div>
          )}
        </div>
      </>
    )
  ) : (
    <></>
  );
};
